/**
 * Pages editor.
 *
 * Layout: 2-column on lg+
 *   ┌──────────────────────────────┬────────────┐
 *   │ Title (large input)          │ Sidebar:   │
 *   │ Slug (kbd-styled)            │  - Status  │
 *   │ TipTap WYSIWYG               │  - Cover   │
 *   │ SEO panel (collapsible)      │  - SEO     │
 *   │                              │  - History │
 *   └──────────────────────────────┴────────────┘
 *
 * Autosaves every 2s after the user stops typing. Manual "Save",
 * "Publish/Unpublish", "Preview", "Open revisions" in the topbar.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Eye, EyeOff, History, ImagePlus, Save, ExternalLink, X as XIcon,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { TipTapEditor } from "@/components/editor/TipTapEditor";
import { MediaPicker } from "@/components/editor/MediaPicker";
import { StatusBadge } from "@/components/StatusBadge";
import { PageHeader } from "@/components/PageHeader";
import { useAutosave, formatSavedAt } from "@/hooks/useAutosave";
import { slugify } from "@/hooks/useSlug";
import { cn } from "@/lib/cn";

export const Route = createFileRoute("/_app/content/pages/$id")({
  component: PageEditor,
});

interface PageDetail {
  id: number;
  slug: string;
  title: string;
  hero_json: string | null;
  body_md: string | null;
  seo_title: string | null;
  seo_description: string | null;
  og_image_r2_key: string | null;
  status: "draft" | "published" | "archived";
  published_at: number | null;
  updated_at: number;
  version: number;
}

interface Revision {
  id: number;
  ts: number;
  updated_by: number | null;
}

const schema = z.object({
  title: z.string().min(1).max(200),
  slug: z.string().min(1).max(120).regex(/^[a-z0-9-]+$/),
  body_md: z.string().max(2_000_000).nullable(),
  seo_title: z.string().max(200).nullable(),
  seo_description: z.string().max(500).nullable(),
  og_image_r2_key: z.string().max(500).nullable(),
});
type FormValues = z.infer<typeof schema>;

const apiBase = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";
const r2PublicUrl = (key: string) => `${apiBase}/admin/media/file/${key}`;

function PageEditor() {
  const { id } = Route.useParams();
  const { can } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [revisionsOpen, setRevisionsOpen] = useState(false);
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);

  const { data: page, isLoading } = useQuery({
    queryKey: ["pages", "detail", id],
    queryFn: () => api.get<PageDetail>(`/admin/pages/${id}`),
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: page
      ? {
          title: page.title,
          slug: page.slug,
          body_md: page.body_md,
          seo_title: page.seo_title,
          seo_description: page.seo_description,
          og_image_r2_key: page.og_image_r2_key,
        }
      : undefined,
  });
  const { register, handleSubmit, watch, setValue, control, formState: { errors, isDirty } } = form;

  const updateMut = useMutation({
    mutationFn: (v: Partial<FormValues>) => api.patch(`/admin/pages/${id}`, v),
    // List only — refetching the detail would feed the server row back through
    // useForm's `values` and reset the form mid-edit. See events editor.
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pages", "list"] }); },
  });
  const publishMut = useMutation({
    mutationFn: () => api.post(`/admin/pages/${id}/publish`),
    onSuccess: () => { toast.success("Published"); qc.invalidateQueries({ queryKey: ["pages"] }); },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Publish failed"),
  });
  const unpublishMut = useMutation({
    mutationFn: () => api.post(`/admin/pages/${id}/unpublish`),
    onSuccess: () => { toast.success("Unpublished"); qc.invalidateQueries({ queryKey: ["pages"] }); },
  });

  const formValues = watch();
  const { status: saveStatus, savedAt } = useAutosave({
    value: formValues,
    enabled: !!page && form.formState.isValid && isDirty,
    delayMs: 1500,
    onSave: (v) => updateMut.mutateAsync(v),
  });

  // Sync slug when title changes (until user touches slug manually)
  const slugTouched = useRef(false);
  const lastTitle = useRef("");
  useEffect(() => {
    const t = formValues.title;
    if (!t || slugTouched.current) return;
    if (t === lastTitle.current) return;
    lastTitle.current = t;
    setValue("slug", slugify(t));
  }, [formValues.title, setValue]);

  if (isLoading || !page) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={page.title || "Untitled"}
        back={{ href: "/content/pages", label: "Pages" }}
        actions={
          <>
            <span className="text-xs text-muted-foreground">
              {saveStatus === "saving" && "Saving…"}
              {saveStatus === "saved" && `Saved ${formatSavedAt(savedAt)}`}
              {saveStatus === "dirty" && "Unsaved changes"}
              {saveStatus === "error" && <span className="text-destructive">Save failed</span>}
            </span>
            <Button variant="outline" size="sm" onClick={() => setRevisionsOpen(true)}>
              <History className="h-4 w-4" />
              History
            </Button>
            <Button variant="outline" size="sm" asChild>
              <a href={`/${page.slug}?preview=1`} target="_blank" rel="noreferrer">
                <ExternalLink className="h-4 w-4" />
                Preview
              </a>
            </Button>
            <Button variant="outline" size="sm" disabled={!can("pages", "update")} onClick={handleSubmit((v) => updateMut.mutateAsync(v).then(() => toast.success("Saved")))}>
              <Save className="h-4 w-4" />
              Save
            </Button>
            {page.status === "published" ? (
              <Button variant="secondary" size="sm" disabled={!can("pages", "publish")} onClick={() => unpublishMut.mutate()}>
                <EyeOff className="h-4 w-4" />
                Unpublish
              </Button>
            ) : (
              <Button size="sm" disabled={!can("pages", "publish")} onClick={() => publishMut.mutate()}>
                <Eye className="h-4 w-4" />
                Publish
              </Button>
            )}
          </>
        }
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        {/* Main editor column */}
        <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div>
                <Label htmlFor="title" className="sr-only">Title</Label>
                <Input
                  id="title"
                  className="h-12 border-0 px-0 text-3xl font-light tracking-tight focus-visible:ring-0"
                  placeholder="Page title"
                  {...register("title")}
                />
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>krce.ac.in/</span>
                <Input
                  className="h-7 max-w-[300px] border-0 bg-secondary px-2 font-mono text-xs"
                  {...register("slug", {
                    onChange: () => { slugTouched.current = true; },
                  })}
                />
                {errors.slug && <span className="text-destructive text-xs">{errors.slug.message}</span>}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="px-0 pt-0 pb-0">
              <Controller
                control={control}
                name="body_md"
                render={({ field }) => (
                  <TipTapEditor
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    placeholder="Write the body of the page…"
                    surface="wide"
                    className="border-0 shadow-none"
                  />
                )}
              />
            </CardContent>
          </Card>

          <Tabs defaultValue="seo">
            <TabsList>
              <TabsTrigger value="seo">SEO &amp; sharing</TabsTrigger>
              <TabsTrigger value="hero">Hero block</TabsTrigger>
            </TabsList>
            <TabsContent value="seo">
              <Card>
                <CardContent className="space-y-4 pt-6">
                  <div className="space-y-2">
                    <Label htmlFor="seo_title">SEO title</Label>
                    <Input
                      id="seo_title"
                      placeholder={page.title}
                      maxLength={200}
                      {...register("seo_title")}
                    />
                    <p className="text-xs text-muted-foreground">Used in browser tab and Google results. Falls back to the page title if blank.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="seo_description">SEO description</Label>
                    <Textarea
                      id="seo_description"
                      placeholder="Short summary that shows in Google results and social previews (155 chars max recommended)."
                      maxLength={500}
                      rows={3}
                      {...register("seo_description")}
                    />
                    <p className="text-xs text-muted-foreground">{(watch("seo_description")?.length ?? 0)} / 160 recommended</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Social preview image</Label>
                    {watch("og_image_r2_key") ? (
                      <div className="flex items-start gap-3">
                        <img src={r2PublicUrl(watch("og_image_r2_key")!)} alt="" className="h-32 w-auto rounded-md border object-cover" />
                        <div className="flex flex-col gap-1.5">
                          <Button type="button" variant="outline" size="sm" onClick={() => setCoverPickerOpen(true)}>
                            <ImagePlus className="h-4 w-4" />
                            Replace
                          </Button>
                          <Button type="button" variant="ghost" size="sm" onClick={() => setValue("og_image_r2_key", null, { shouldDirty: true })}>
                            <XIcon className="h-4 w-4" />
                            Remove
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Button type="button" variant="outline" onClick={() => setCoverPickerOpen(true)}>
                        <ImagePlus className="h-4 w-4" />
                        Choose image
                      </Button>
                    )}
                    <p className="text-xs text-muted-foreground">1200×630 recommended. Used by Open Graph + Twitter cards.</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="hero">
              <Card>
                <CardContent className="space-y-3 pt-6">
                  <p className="text-sm text-muted-foreground">
                    Hero blocks accept structured JSON. Schema is page-specific.
                    For now, edit raw JSON; in a follow-up we'll ship a visual builder.
                  </p>
                  <Controller
                    control={control}
                    name={"hero_json" as never}
                    render={({ field }) => (
                      <Textarea
                        rows={10}
                        placeholder={`{\n  "headline": "Founded 2010",\n  "image_r2_key": "..."\n}`}
                        value={field.value ? (typeof field.value === "string" ? field.value : JSON.stringify(field.value, null, 2)) : ""}
                        onChange={(e) => field.onChange(e.target.value)}
                        className="font-mono text-xs"
                      />
                    )}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </form>

        {/* Sidebar */}
        <aside className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Current</span>
                <StatusBadge status={page.status} />
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Published</span>
                <span className="text-foreground">{page.published_at ? format(page.published_at * 1000, "MMM d, yyyy") : "—"}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Updated</span>
                <span className="text-foreground">{format(page.updated_at * 1000, "MMM d, yyyy")}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Version</span>
                <span className="font-mono text-xs">v{page.version}</span>
              </div>
              <Separator />
              <Link
                to="/content/pages" as never
                className="block text-xs text-muted-foreground hover:text-primary"
              >
                ← All pages
              </Link>
            </CardContent>
          </Card>
        </aside>
      </div>

      <RevisionsDialog open={revisionsOpen} onOpenChange={setRevisionsOpen} pageId={page.id} />
      <MediaPicker
        open={coverPickerOpen}
        onOpenChange={setCoverPickerOpen}
        onPick={(url) => {
          // store the R2 key (relative to /admin/media/file/) so the public site can render any size
          const key = url.split("/admin/media/file/")[1] ?? "";
          setValue("og_image_r2_key", key, { shouldDirty: true });
          setCoverPickerOpen(false);
        }}
      />
    </div>
  );
}

function RevisionsDialog({ open, onOpenChange, pageId }: { open: boolean; onOpenChange: (b: boolean) => void; pageId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ["pages", "revisions", pageId],
    queryFn: () => api.get<{ items: Revision[] }>(`/admin/pages/${pageId}/revisions`),
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Revisions</DialogTitle>
          <DialogDescription>Each save snapshots the prior version. The most recent shows up first.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[50vh] overflow-y-auto">
          {isLoading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
          ) : data?.items.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No prior revisions yet.</p>
          ) : (
            <ul className="divide-y">
              {data?.items.map((r) => (
                <li key={r.id} className={cn("flex items-center justify-between py-3 text-sm")}>
                  <div>
                    <p className="font-medium">{format(r.ts * 1000, "MMM d, yyyy 'at' h:mm a")}</p>
                    <p className="text-xs text-muted-foreground">By user #{r.updated_by ?? "—"}</p>
                  </div>
                  <Button variant="outline" size="sm" disabled>
                    Restore (coming soon)
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
